import { allocationsRestricted } from '@ck';
import { ColorMode, type IColor } from '@color';
import { LRUCache } from 'lru-cache';
import { type LayoutTextOptions } from './ckTextUtils';
import { layoutParagraph } from './layoutParagraph';
import { Paragraph } from './Paragraph';

/**
 * Caches CanvasKit `Paragraph` objects keyed by layout id and color mode.
 *
 * A text layout pass uses {@link layoutText} to measure strings and store
 * paragraphs in this cache. Each entry tracks the text, its adaptive color,
 * and layout options. When a `Paragraph` for a different {@link ColorMode}
 * is requested, the cache re-layouts the text with the new color and stores
 * the result, allowing color mode switches without re-running layout.
 */

export class ParagraphCache {
  private cache1: LRUCache<string, Cache1Entry>;
  private cache2: LRUCache<string, Cache2Entry>;

  private static cache2Key(id: string, colorMode: ColorMode) {
    return `${id}-${colorMode}`;
  }

  constructor() {
    this.cache1 = new LRUCache({
      max: 10000,
      dispose: entry => {
        const darkKey = ParagraphCache.cache2Key(entry.id, ColorMode.DARK);
        const darkEntry = this.cache2.get(darkKey);
        if (darkEntry) {
          this.cache2.delete(darkKey);
        }
        const lightKey = ParagraphCache.cache2Key(entry.id, ColorMode.LIGHT);
        const lightEntry = this.cache2.get(lightKey);
        if (lightEntry) {
          this.cache2.delete(lightKey);
        }
      }
    });
    this.cache2 = new LRUCache({
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      max: allocationsRestricted ? 10 : 500,
      dispose: entry => {
        entry.paragraph.delete();
      }
    });
  }

  set(
    id: string,
    text: string,
    color: IColor,
    colorMode: ColorMode,
    layoutOptions: LayoutTextOptions,
    paragraph: Paragraph
  ) {
    this.cache1.set(id, { id, text, adaptiveColor: color, layoutOptions });
    const key = ParagraphCache.cache2Key(id, colorMode);
    this.cache2.set(key, { paragraph });
  }

  get(id: string, colorMode: ColorMode): Paragraph | undefined {
    // console.log(`💦 cache get ${id} colorMode: ${colorMode}`);
    const entry1 = this.cache1.get(id);
    if (entry1 === undefined) {
      // console.log(`💦 cache1 miss`);
      return undefined;
    }
    const key = ParagraphCache.cache2Key(id, colorMode);
    const entry2 = this.cache2.get(key);
    let paragraph = entry2?.paragraph;
    if (paragraph === undefined) {
      // console.log(`💦 cache2 miss`);
      const adaptiveColor = entry1.adaptiveColor;
      // console.log(`💦 adaptiveColor: ${adaptiveColor}`);
      const color = adaptiveColor.realize(colorMode);
      const { paragraph: p } = layoutParagraph(entry1.text, color, entry1.layoutOptions);
      paragraph = p;
      this.cache2.set(key, { paragraph });
      // entry.paragraphs.set(colorMode, p);
      // this.cache.set(id, entry);
    }
    return paragraph;
  }

  clear() {
    this.cache1.clear();
    this.cache2.clear();
  }
}

export interface Cache1Entry {
  id: string;
  text: string;
  adaptiveColor: IColor;
  layoutOptions: LayoutTextOptions;
}

export interface Cache2Entry {
  paragraph: Paragraph;
}
